import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  BarChart3, TrendingUp, Flame, Zap, Clock, Award, 
  Dumbbell, Target, Trophy, Activity, Heart, Calendar
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [weeklyVolume, setWeeklyVolume] = useState([]);
  const [categories, setCategories] = useState([]);
  const [records, setRecords] = useState([]);
  const [heatmap, setHeatmap] = useState({ grid: [], total_active_days: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [ovRes, wvRes, catRes, recRes, hmRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`, { headers }),
        axios.get(`${API}/analytics/weekly-volume?weeks=8`, { headers }),
        axios.get(`${API}/analytics/category-breakdown`, { headers }),
        axios.get(`${API}/analytics/personal-records`, { headers }),
        axios.get(`${API}/analytics/consistency-heatmap?days=90`, { headers }),
      ]);
      setOverview(ovRes.data);
      setWeeklyVolume(wvRes.data.weeks || []);
      setCategories(catRes.data.categories || []);
      setRecords(recRes.data.records || []);
      setHeatmap(hmRes.data);
    } catch (err) {
      console.error("Analytics fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-orange-500 text-xl font-heading">Loading analytics...</div>
      </div>
    );
  }

  const RECORD_ICONS = { trending_up: TrendingUp, clock: Clock, zap: Zap, flame: Flame, award: Award, dumbbell: Dumbbell };
  const totalCatCount = categories.reduce((a, c) => a + c.count, 0) || 1;
  const maxWeekly = Math.max(...weeklyVolume.map(w => w.workouts), 1);

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-6 lg:p-8 relative" data-testid="analytics-dashboard">
      <div className="noise-overlay" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="mb-2">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF4500] mb-2">Performance Insights</p>
          <h1 className="text-5xl md:text-7xl font-heading text-white uppercase tracking-tight leading-none">Analytics</h1>
        </div>

        {/* Top Stats */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/[0.06]">
            {[
              { label: "TOTAL WORKOUTS", value: overview.total_workouts, icon: Dumbbell, color: "#FF4500" },
              { label: "THIS WEEK", value: overview.week_workouts, icon: Activity, color: "#FF4500" },
              { label: "CURRENT STREAK", value: `${overview.current_streak}d`, icon: Flame, color: "#CCFF00" },
              { label: "EST. CALORIES", value: overview.est_calories_burned.toLocaleString(), icon: Heart, color: "#FF4500" },
            ].map((stat, i) => (
              <div key={i} className="bg-[#0A0A0A] p-5 halftone">
                <stat.icon className="w-4 h-4 mb-3" style={{ color: stat.color }} />
                <p className="text-4xl md:text-5xl font-heading text-white leading-none">{stat.value}</p>
                <p className="text-[9px] font-mono text-white/30 uppercase tracking-[0.2em] mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-px bg-white/[0.06]">
          {/* Weekly Volume Chart */}
          <div className="bg-[#0A0A0A] p-6">
            <p className="text-[9px] font-mono text-white/30 uppercase tracking-[0.2em] mb-4">Weekly Workout Volume</p>
            <div className="flex items-end gap-2 h-40" data-testid="weekly-volume-chart">
              {weeklyVolume.map((w, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[10px] font-mono text-white/50 font-bold">{w.workouts}</span>
                  <div
                    className="w-full bg-[#FF4500] transition-all hover:bg-[#FF6326]"
                    style={{ height: `${Math.max((w.workouts / maxWeekly) * 100, 4)}%` }}
                  />
                  <span className="text-[8px] font-mono text-white/20">{w.week}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Category Breakdown */}
          <div className="bg-[#0A0A0A] p-6">
            <p className="text-[9px] font-mono text-white/30 uppercase tracking-[0.2em] mb-4">Category Breakdown</p>
            <div className="space-y-5" data-testid="category-breakdown">
              {categories.map((cat, i) => {
                const pct = Math.round((cat.count / totalCatCount) * 100);
                return (
                  <div key={i}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-white font-medium">{cat.name}</span>
                      <span className="text-sm font-mono text-white/50">{pct}%</span>
                    </div>
                    <div className="h-2 bg-white/[0.06] overflow-hidden">
                      <div
                        className="h-full transition-all"
                        style={{ width: `${pct}%`, backgroundColor: cat.color }}
                      />
                    </div>
                    <p className="text-[9px] font-mono text-white/20 mt-1">{cat.count} drills completed</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Personal Records */}
        <Card className="bg-zinc-900/80 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold text-white flex items-center gap-2">
              <Trophy className="w-4 h-4 text-yellow-500" />
              Personal Records
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="personal-records">
              {records.map((rec, i) => {
                const Icon = RECORD_ICONS[rec.icon] || Award;
                return (
                  <div key={i} className="p-3 bg-zinc-800/50 rounded-sm border border-zinc-700 text-center">
                    <Icon className="w-5 h-5 mx-auto text-yellow-500 mb-1" />
                    <p className="text-lg font-black text-white">{rec.value}</p>
                    <p className="text-[9px] text-zinc-500 uppercase tracking-wider">{rec.label}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Consistency Heatmap */}
        <Card className="bg-zinc-900/80 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold text-white flex items-center gap-2">
              <Calendar className="w-4 h-4 text-emerald-500" />
              90-Day Consistency
              <Badge className="bg-emerald-500/10 text-emerald-400 text-xs">
                {heatmap.total_active_days} active days
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-[3px]" data-testid="consistency-heatmap">
              {heatmap.grid.map((day, i) => {
                const colors = ["bg-zinc-800", "bg-emerald-900/50", "bg-emerald-700/60", "bg-emerald-500/70", "bg-emerald-400"];
                return (
                  <div
                    key={i}
                    className={`w-3 h-3 rounded-[2px] ${colors[day.level]} transition-colors`}
                    title={`${day.date}: ${day.count} workouts`}
                  />
                );
              })}
            </div>
            <div className="flex items-center gap-2 mt-3 text-[10px] text-zinc-500">
              <span>Less</span>
              {["bg-zinc-800", "bg-emerald-900/50", "bg-emerald-700/60", "bg-emerald-500/70", "bg-emerald-400"].map((c, i) => (
                <div key={i} className={`w-3 h-3 rounded-[2px] ${c}`} />
              ))}
              <span>More</span>
            </div>
          </CardContent>
        </Card>

        {/* Additional Stats Row */}
        {overview && (
          <div className="grid grid-cols-3 gap-3">
            <Card className="bg-zinc-900/80 border-zinc-800">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-black text-emerald-400">{overview.total_duration_min}</p>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Total Minutes Trained</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/80 border-zinc-800">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-black text-blue-400">{overview.vertical_gained}\"</p>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Vertical Gained</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/80 border-zinc-800">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-black text-purple-400">{overview.total_badges}</p>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Badges Earned</p>
              </CardContent>
            </Card>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AnalyticsDashboard;
